<template>
  <div class="training-plan-card">
    <div class="card-image" :class="{ 'card-image--board': showBoardPreview }" @click="$emit('open', drill)">
      <img v-if="drill.imageURL" :src="getImgUrl(drill.imageURL)" :alt="drill.name" />
      <div v-else-if="board" class="card-board-preview">
        <img v-if="board.thumbnail" :src="board.thumbnail" :alt="drill.name" class="board-thumb-img" />
        <TacticalBoardCanvas v-else :title="board.title || 'Board'" :savedElements="board.elements || []"
          :background="board.background || 'full-pitch'" :savedBackgroundImage="board.background_image || ''"
          :orientation="board.orientation || 'portrait'"
          :imageTransform="board.image_transform || { x: 0, y: 0, scale: 1 }" :editMode="false" />
      </div>
      <div v-else class="card-image-placeholder">
        <i class="bi bi-clipboard-check"></i>
      </div>
      <span v-if="badge" class="card-badge" :class="badge.className">
        <i v-if="badge.icon" :class="badge.icon"></i> {{ badge.label }}
      </span>
      <span v-else class="card-badge" :class="sectionBadgeClass">{{ drill.section }}</span>
    </div>

    <div class="card-body" @click="$emit('open', drill)">
      <h3 class="card-title">{{ drill.name }}</h3>
      <slot name="meta">
        <div class="card-tags" v-if="drill.category || drill.training_type || drill.age_level">
          <span v-if="drill.training_type" class="tag-chip tag-type" :style="{
            background: typeColor(drill.training_type) + '22',
            color: typeColor(drill.training_type),
            borderColor: typeColor(drill.training_type) + '44',
          }">{{ drill.training_type }}</span>
          <span v-if="drill.category" class="tag-chip tag-category">{{ drill.category }}</span>
          <span v-if="drill.age_level" class="tag-chip tag-level">{{ drill.age_level }}</span>
        </div>
      </slot>
      <p v-if="introLength > 0 && drill.introText" class="card-intro">
        {{ truncateText(drill.introText, introLength) }}
      </p>
    </div>

    <a v-if="drill.pdf_url" :href="getFilePath(drill.pdf_url)" target="_blank" rel="noopener" class="card-pdf-link"
      @click.stop>
      <i class="bi bi-file-pdf"></i> View PDF
    </a>

    <slot name="actions"></slot>
  </div>
</template>

<script>
import TacticalBoardCanvas from "../TacticalBoard/TacticalBoardCanvas.vue";
import { TRAINING_SECTIONS } from "@/constants/training-sections.js";

/**
 * Shared drill/training-plan card used by the library grid, the programme
 * drill picker, and the programme detail view.
 *
 * The image area shows (in priority order): the drill's uploaded image, its
 * first linked tactical board (pre-rendered thumbnail when available, live
 * canvas otherwise), or a placeholder icon.
 */
export default {
  name: "DrillCard",
  components: { TacticalBoardCanvas },
  props: {
    drill: { type: Object, required: true },
    board: { type: Object, default: null },
    introLength: { type: Number, default: 120 },
    // Optional badge override: { label, className, icon }. Defaults to the
    // drill's section badge.
    badge: { type: Object, default: null },
  },
  emits: ["open"],
  computed: {
    showBoardPreview() {
      return !this.drill.imageURL && !!this.board;
    },
    sectionBadgeClass() {
      const classes = {
        [TRAINING_SECTIONS.TRAINING]: "badge-training",
        [TRAINING_SECTIONS.MENTAL]: "badge-mental",
        [TRAINING_SECTIONS.NUTRITION]: "badge-nutrition",
        [TRAINING_SECTIONS.RECOVERY]: "badge-recovery",
      };
      return classes[this.drill.section] || "badge-default";
    },
  },
  methods: {
    getImgUrl(pic) {
      return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
    },
    getFilePath(path) {
      if (!path) return "";
      return path.startsWith("http") ? path : "/" + path.replace(/^\//, "");
    },
    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;
    },
    typeColor(type) {
      const colors = {
        Technical: "#3b82f6",
        Tactical: "#8b5cf6",
        Physical: "#f97316",
        Mental: "#10b981",
      };
      return colors[type] || "#64748b";
    },
  },
};
</script>

<style scoped lang="scss">
.training-plan-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .card-image {
    position: relative;
    height: 160px;
    overflow: hidden;
    cursor: pointer;

    &.card-image--board {
      height: 200px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .card-board-preview {
      width: 100%;
      height: 100%;
      pointer-events: none;
      background: #fff;

      .board-thumb-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      :deep(.tactical-board-slide) {
        height: 100%;
        padding: 0;
        border: none;
        box-shadow: none;
        border-radius: 0;
        /* Kill the dark navy gradient so the pitch isn't framed by dark bars */
        background: none;
      }

      :deep(.slide-header),
      :deep(.notes-panel),
      :deep(.notes-display) {
        display: none;
      }

      :deep(.board-content) {
        height: 100%;
      }

      :deep(.canvas-wrap) {
        height: 100%;
        overflow: hidden;
      }

      :deep(.drawing-canvas) {
        width: 100%;
        height: 100%;
        max-height: none;
        border-radius: 0;
        box-shadow: none;
      }
    }

    .card-badge {
      position: absolute;
      top: 12px;
      right: 12px;
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 0.7rem;
      font-weight: 600;
      color: white;

      &.badge-training {
        background: #4299e1;
      }

      &.badge-mental {
        background: #9f7aea;
      }

      &.badge-nutrition {
        background: #48bb78;
      }

      &.badge-recovery {
        background: #ed8936;
      }

      &.badge-default {
        background: #718096;
      }

      &.badge-added {
        background: rgba(16, 185, 129, 0.85);
        color: #fff;
      }
    }
  }

  .card-image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);

    i {
      font-size: 2.5rem;
      color: #94a3b8;
    }
  }

  .card-body {
    padding: 1rem;
    cursor: pointer;

    .card-title {
      font-size: 1.05rem;
      font-weight: 600;
      margin-bottom: 0.4rem;
      color: #1a202c;
    }

    .card-intro {
      font-size: 0.85rem;
      color: #4a5568;
      line-height: 1.4;
      margin: 0;
    }
  }

  .card-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin: 4px 0 6px;
  }

  .tag-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 7px;
    border-radius: 4px;
    font-size: 0.62rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1.6;
  }

  .tag-type {
    border: 1px solid;
  }

  .tag-category {
    background: rgba(102, 126, 234, 0.15);
    color: #818cf8;
  }

  .tag-level {
    background: rgba(245, 158, 11, 0.15);
    color: #fbbf24;
  }

  .card-pdf-link {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.6rem 1rem;
    border-top: 1px solid #e2e8f0;
    font-size: 0.8rem;
    font-weight: 600;
    color: #dc2626;
    text-decoration: none;

    &:hover {
      background: #fef2f2;
    }

    i {
      font-size: 1rem;
    }
  }

  /* Slotted admin action rows keep the parent's markup but need the card's
     border treatment */
  :slotted(.card-actions) {
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid #e2e8f0;
    justify-content: flex-end;
  }
}

/* Picker state: classes placed on the component root by callers */
.training-plan-card.drill-picker-card {
  cursor: pointer;
  transition: box-shadow 0.15s, opacity 0.15s;

  &.drill-already-added {
    opacity: 0.8;
    border-color: #86efac;
    background: #f0fdf4;
  }
}
</style>
